import DOMPurify from 'dompurify'

/**
 * A descrição da missão é autorada no editor Rich Text do admin e chega aqui
 * como HTML. Renderizar HTML de terceiros exige sanitização — mesmo autorado só
 * por admin, é defesa contra conta comprometida ou `javascript:` colado por
 * engano (mesmo motivo do LegalDocumentContent).
 *
 * O campo também guarda descrições antigas em texto simples, com quebras em
 * `\n` e URLs cruas que sempre viraram link. Esse comportamento é preservado:
 * texto sem tag nenhuma é escapado, linkificado e convertido em `<br>`.
 */

/** Alinhado ao toolbar do editor no admin (DESCRIPTION_EDITOR_MODULES). */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'a'
]

/*
 * `data-list` distingue lista com marcador de lista numerada no Quill 2, que
 * emite as duas como <ol>. Sem ele, marcadores virariam números.
 */
const ALLOWED_ATTR = ['href', 'target', 'rel', 'data-list']

/** Uma tag qualquer — só o conteúdo vindo do editor tem. */
const HTML_TAG = /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\/?>/i

const URL_PATTERN =
  /(?:(?:https?:\/\/)?(?:[\w-]+\.)+[\w-]{2,}(?:\/[\w.,@?^=%&:/~+#-]*)?)/gi

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;'
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, char => ESCAPES[char])
}

/*
 * Sem `class`: o allowlist não deixa atributo de estilo passar (o editor
 * emitiria `ql-*` junto), então a aparência do link vem do container que
 * renderiza a descrição.
 */
function anchor(url: string): string {
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`

  return (
    `<a href="${escapeHtml(href)}" target="_blank" ` +
    `rel="noopener noreferrer">${escapeHtml(url)}</a>`
  )
}

/** `true` quando o valor veio do editor, `false` para o texto simples legado. */
export function isRichText(value: string): boolean {
  return HTML_TAG.test(value)
}

/**
 * Converte o texto simples legado no HTML equivalente: URLs viram links e
 * quebras de linha viram `<br>`. O escape roda por segmento, entre uma URL e a
 * seguinte — escapar a string inteira antes transformaria o `&` de uma query
 * em `&amp;` e cortaria o href no meio.
 */
export function plainTextToRichText(value: string): string {
  let html = ''
  let cursor = 0

  for (const match of value.matchAll(URL_PATTERN)) {
    const url = match[0]
    const start = match.index ?? 0

    html += escapeHtml(value.slice(cursor, start))
    html += anchor(url)
    cursor = start + url.length
  }

  html += escapeHtml(value.slice(cursor))

  return html.replace(/\r\n?/g, '\n').replace(/\n/g, '<br>')
}

/** HTML pronto para `dangerouslySetInnerHTML`, venha do editor ou do legado. */
export function sanitizeRichText(value: string | null | undefined): string {
  if (!value) return ''

  const html = isRichText(value) ? value : plainTextToRichText(value)

  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
}
